import { CubePresenter, FlipIntent, Logger, NOOP_VOID, ScopeSlot } from 'src/3rds/cube'
import { MainPresenter } from 'src/main/Main.presenter'
import { AppStorage } from 'src/utils'
import { ServiceError } from 'src/utils/ServiceError'
import { isValidCNPJ } from 'src/modules/cadastro-lote/CadastroLote.service'
import { GrupoManualKeys } from './GrupoManual.keys'
import {
    Empresa,
    Evento,
    GRUPOS_POR_PAGINA,
    GrupoManualScope,
    Impacto,
    MOTIVO_MINIMO,
    OrdemGrupos,
    Simulacao,
    Situacao,
    Sugestao
} from './GrupoManual.scopes'
import { GrupoManualService } from './GrupoManual.service'

const LOG = Logger.get('GrupoManualPresenter')
const service = GrupoManualService.singleton()
const appStorage = AppStorage.singleton()

// Espera o operador parar de digitar antes de consultar o servidor.
const DEBOUNCE_MS = 300

// Os grupos manuais entram no grupo econômico de todos os leads: só admin/manager.
export const ROLES_AUTORIZADOS = new Set(['admin', 'manager'])

export function podeGerirGruposManuais() {
    return ROLES_AUTORIZADOS.has(`${appStorage.userRole ?? ''}`)
}

/** Erros de negócio do servidor (CNPJ inválido, fora da base, grupos em conflito) aparecem na tela. */
function mensagemDeNegocio(error: unknown): string | undefined {
    if (error instanceof ServiceError && [400, 404, 409].includes(error.statusCode)) {
        return error.message
    }
    return undefined
}

export type Frase = { texto: string; alerta?: boolean }

const ROTULO_FASE: Record<string, string> = { A: 'Abordagem', R: 'Reserva', K: 'Cota', C: 'Cliente' }

/** Frases sobre os negócios que passam a pesar nas consultas de um lado. */
export function frasesDeImpacto(lado: string, impacto: Impacto | undefined): Frase[] {
    if (!impacto || impacto.empresasNovas === 0) return []
    const frases: Frase[] = []
    const fortes = impacto.negocios.filter((n) => n.fase !== 'A')
    const abordagens = impacto.negocios.filter((n) => n.fase === 'A')

    for (const negocio of fortes.slice(0, 4)) {
        const empresa = negocio.razaoSocial ?? negocio.cnpj
        frases.push(
            negocio.fase === 'C'
                ? {
                      texto: `${empresa} já é cliente: consultas a ${lado} passam a responder “grupo já é cliente”.`,
                      alerta: true
                  }
                : {
                      texto:
                          `${empresa} está em ${ROTULO_FASE[negocio.fase] ?? negocio.fase}` +
                          `${
                              negocio.parceiro ? ` de ${negocio.parceiro}` : ''
                          }: consultas a ${lado} passam a ser bloqueadas por esse negócio.`,
                      alerta: true
                  }
        )
    }
    if (fortes.length > 4) {
        frases.push({ texto: `E mais ${fortes.length - 4} negócios em Reserva, Cota ou Cliente.`, alerta: true })
    }
    if (abordagens.length > 0) {
        const empresas = [...new Set(abordagens.map((n) => n.razaoSocial ?? n.cnpj))]
        frases.push({
            texto:
                `${abordagens.length} ${abordagens.length === 1 ? 'negócio' : 'negócios'} em Abordagem em ${empresas
                    .slice(0, 2)
                    .join(', ')}` +
                `${
                    empresas.length > 2 ? ' e outras' : ''
                }: consultas de outros parceiros a ${lado} passam a esperar o prazo de 45 dias.`,
            alerta: true
        })
    }
    if (impacto.negocios.length === 0) {
        frases.push({
            texto: `${lado} ganha ${impacto.empresasNovas} ${
                impacto.empresasNovas === 1 ? 'empresa' : 'empresas'
            } no grupo, sem negócios em andamento.`
        })
    }
    return frases
}

/**
 * O que a ligação vai fazer, em frases para o diálogo, e o motivo de não poder ligar, se houver. Espelha o
 * servidor: cria um grupo, inclui uma empresa no grupo da outra ou une os dois grupos. Com a simulação,
 * acrescenta os negócios que passam a pesar em cada lado.
 */
export function oQueMuda(
    situacao: Situacao,
    outra: Empresa,
    simulacao?: Simulacao
): { modo?: 'criar' | 'incluir' | 'unir'; frases: Frase[]; impedimento?: string } {
    const empresa = situacao.empresa
    const nomeOutra = outra.razaoSocial ?? 'A outra empresa'
    const nomeEmpresa = empresa.razaoSocial ?? 'A empresa'

    if (outra.cnpjMatriz === empresa.cnpjMatriz) {
        return { frases: [], impedimento: 'É a mesma empresa (mesma matriz).' }
    }
    if (empresa.grupoAtual != null && empresa.grupoAtual === outra.grupoAtual) {
        return { frases: [], impedimento: 'As duas já estão no mesmo grupo manual.' }
    }

    const frases: Frase[] = []
    let modo: 'criar' | 'incluir' | 'unir'
    if (empresa.grupoAtual != null && outra.grupoAtual != null) {
        modo = 'unir'
        const u = simulacao?.unir
        frases.push({
            texto: u
                ? `Os grupos manuais ${u.grupoFica} (${u.qtdeFica} empresas) e ${u.grupoSai} (${
                      u.qtdeSai
                  } empresas) viram um só, com ${u.qtdeFica + u.qtdeSai} empresas. Fica o número ${
                      u.grupoFica
                  }, o mais antigo, com o histórico dos dois.`
                : `Os grupos manuais ${empresa.grupoAtual} e ${outra.grupoAtual} viram um só.`
        })
    } else if (empresa.grupoAtual != null) {
        modo = 'incluir'
        const qtde = situacao.grupoManual?.qtdeEmpresas ?? 0
        frases.push({
            texto: `${nomeOutra} entra no grupo manual de ${nomeEmpresa}, que passa de ${qtde} para ${
                qtde + 1
            } empresas.`
        })
    } else if (outra.grupoAtual != null) {
        modo = 'incluir'
        frases.push({
            texto: `${nomeEmpresa} entra no grupo manual ${outra.grupoAtual}, de que ${nomeOutra} já faz parte.`
        })
    } else {
        modo = 'criar'
        frases.push({ texto: `Um grupo manual novo é criado com ${nomeEmpresa} e ${nomeOutra}.` })
    }

    frases.push(...frasesDeImpacto(nomeEmpresa, simulacao?.paraEmpresa))
    frases.push(...frasesDeImpacto(nomeOutra, simulacao?.paraOutra))
    frases.push({ texto: 'Negócios que já existem não mudam; vale a partir da próxima consulta de CNPJ.' })
    return { modo, frases }
}

/** Texto com letras é busca por nome; só dígitos e pontuação, CNPJ. */
export function temLetras(texto: string) {
    return /[a-zA-ZÀ-ÿ]/.test(texto)
}

/** O motivo digitado basta para gravar? */
export function motivoSuficiente(motivo: string) {
    return motivo.replace(/\s+/g, ' ').trim().length >= MOTIVO_MINIMO
}

/** Uma frase para o histórico e para a coluna "Última alteração". */
export function descreverEvento(evento: Evento): string {
    const empresa = evento.razaoSocial ?? evento.cnpj ?? 'uma empresa'
    const outra = evento.razaoSocialRelacionada ?? evento.cnpjRelacionado ?? 'outra empresa'
    switch (evento.tipo) {
        case 'criado':
            return `${evento.usuario} criou o grupo ligando ${empresa} a ${outra}`
        case 'incluida':
            return `${evento.usuario} ligou ${empresa} a ${outra}`
        case 'retirada':
            return `${evento.usuario} tirou ${empresa} do grupo`
        case 'unido':
            return `${evento.usuario} uniu o grupo ${evento.grupoIdOrigem ?? ''} a este`.replace('  ', ' ')
        default:
            return `${evento.usuario} alterou o grupo`
    }
}

export class GrupoManualPresenter extends CubePresenter<MainPresenter, GrupoManualScope> {
    private parentSlot: ScopeSlot = NOOP_VOID
    private filtroTimer?: ReturnType<typeof setTimeout>
    private sugestoesTimer?: ReturnType<typeof setTimeout>

    constructor(app: MainPresenter) {
        super(app, new GrupoManualScope())
    }

    public override release() {
        this.#cancelarFiltroAgendado()
        this.#cancelarSugestoesAgendadas()
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new GrupoManualKeys(this.app, intent)

        if (initialization) {
            if (!podeGerirGruposManuais()) {
                this.app.alert('warning', 'Acesso restrito', 'Os grupos manuais são restritos a administradores.')
                keys.allow = false
                return keys.allow
            }

            this.parentSlot = keys.parentSlot
            this.#initialize()
        }

        if (keys.aba !== this.scope.aba) {
            await this.#mostrarAba(keys.aba)
        }
        if (keys.cnpj && keys.cnpj !== this.scope.situacao?.empresa.cnpjInformado) {
            this.scope.busca = keys.cnpj
            await this.#carregarSituacao(keys.cnpj)
        }

        this.parentSlot(this.scope)

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new GrupoManualKeys(this.app, intent)
        keys.aba = this.scope.aba
        keys.cnpj = this.scope.situacao?.empresa.cnpjInformado ?? ''
    }

    #initialize() {
        LOG.debug('initialize')

        this.scope.onAbaChange = this.action(this.#handleAbaChange, this)
        this.scope.onBuscaChange = this.action(this.#handleBuscaChange, this)
        this.scope.onEscolherEmpresa = this.action(this.#handleEscolherEmpresa, this)

        this.scope.onFiltroChange = this.action(this.#handleFiltroChange, this)
        this.scope.onOrdemChange = this.action(this.#handleOrdemChange, this)
        this.scope.onPaginaChange = this.action(this.#handlePaginaChange, this)
        this.scope.onAbrirEmpresa = this.action(this.#handleAbrirEmpresa, this)

        this.scope.onAbrirLigar = this.action(this.#handleAbrirLigar, this)
        this.scope.onFecharLigar = this.action(this.#handleFecharLigar, this)
        this.scope.onCnpjLigarChange = this.action(this.#handleCnpjLigarChange, this)
        this.scope.onEscolherOutra = this.action(this.#handleEscolherOutra, this)
        this.scope.onConfirmarLigar = this.action(this.#handleConfirmarLigar, this)
        this.scope.onMotivoLigarChange = this.action(this.#handleMotivoLigarChange, this)

        this.scope.onAbrirRetirar = this.action(this.#handleAbrirRetirar, this)
        this.scope.onFecharRetirar = this.action(this.#handleFecharRetirar, this)
        this.scope.onConfirmarRetirar = this.action(this.#handleConfirmarRetirar, this)
        this.scope.onMotivoRetirarChange = this.action(this.#handleMotivoRetirarChange, this)
    }

    // :: Abas

    async #handleAbaChange(aba: 'empresa' | 'grupos') {
        await this.#mostrarAba(aba)
        this.updateHistory()
    }

    async #mostrarAba(aba: 'empresa' | 'grupos') {
        this.scope.aba = aba
        if (aba === 'grupos') {
            await this.#carregarLista()
        }
    }

    // :: Por empresa

    async #handleBuscaChange(texto: string) {
        this.scope.busca = texto
        this.scope.erroBusca = undefined

        const cnpj = texto.replace(/\D/g, '')
        if (temLetras(texto)) {
            this.#agendarSugestoes(texto, (sugestoes) => (this.scope.sugestoes = sugestoes))
            return
        }
        this.scope.sugestoes = []
        if (cnpj.length < 14) return
        if (!isValidCNPJ(cnpj)) {
            this.scope.erroBusca = 'CNPJ inválido.'
            return
        }
        await this.#carregarSituacao(cnpj)
        this.updateHistory()
    }

    /** Escolha de uma sugestão da busca por nome. */
    async #handleEscolherEmpresa(cnpj: string) {
        this.scope.sugestoes = []
        this.scope.busca = cnpj
        await this.#carregarSituacao(cnpj)
        this.updateHistory()
    }

    /** Busca por nome com espera: só consulta quando o operador para de digitar. */
    #agendarSugestoes(texto: string, aplicar: (sugestoes: Sugestao[]) => void) {
        this.#cancelarSugestoesAgendadas()
        if (texto.trim().length < 3) {
            aplicar([])
            return
        }
        this.sugestoesTimer = setTimeout(() => {
            this.sugestoesTimer = undefined
            service
                .sugerir(texto.trim())
                .then(aplicar)
                .catch((error) => LOG.error('Buscando empresas por nome', error))
        }, DEBOUNCE_MS)
    }

    #cancelarSugestoesAgendadas() {
        if (this.sugestoesTimer) {
            clearTimeout(this.sugestoesTimer)
            this.sugestoesTimer = undefined
        }
    }

    async #carregarSituacao(cnpj: string) {
        this.scope.carregandoSituacao = true
        try {
            this.scope.situacao = await service.situacao(cnpj)
            this.scope.erroBusca = undefined
        } catch (error) {
            this.scope.situacao = undefined
            const mensagem = mensagemDeNegocio(error)
            if (mensagem) {
                this.scope.erroBusca = mensagem
            } else {
                this.unexpected('Carregando a empresa', error)
            }
        } finally {
            this.scope.carregandoSituacao = false
        }
    }

    // :: Todos os grupos

    async #carregarLista() {
        this.scope.carregandoLista = true
        try {
            const pagina = await service.pesquisar(
                this.scope.filtro.trim(),
                this.scope.ordem,
                this.scope.pagina * GRUPOS_POR_PAGINA,
                GRUPOS_POR_PAGINA
            )
            this.scope.total = pagina.total
            this.scope.grupos = pagina.itens
        } catch (error) {
            this.unexpected('Carregando os grupos manuais', error)
        } finally {
            this.scope.carregandoLista = false
        }
    }

    async #handleFiltroChange(texto: string) {
        this.scope.filtro = texto
        this.#cancelarFiltroAgendado()
        this.filtroTimer = setTimeout(() => {
            this.filtroTimer = undefined
            this.scope.pagina = 0
            this.#carregarLista().catch((error) => LOG.error('Filtrando grupos', error))
        }, DEBOUNCE_MS)
    }

    #cancelarFiltroAgendado() {
        if (this.filtroTimer) {
            clearTimeout(this.filtroTimer)
            this.filtroTimer = undefined
        }
    }

    async #handleOrdemChange(ordem: OrdemGrupos) {
        this.scope.ordem = ordem
        this.scope.pagina = 0
        await this.#carregarLista()
    }

    async #handlePaginaChange(pagina: number) {
        this.scope.pagina = pagina
        await this.#carregarLista()
    }

    /** Da lista para a aba "por empresa", abrindo a empresa principal do grupo. */
    async #handleAbrirEmpresa(cnpj: string) {
        this.scope.aba = 'empresa'
        this.scope.busca = cnpj
        await this.#carregarSituacao(cnpj)
        this.updateHistory()
    }

    // :: Ligar a outra empresa

    async #handleAbrirLigar() {
        this.scope.cnpjLigar = ''
        this.scope.conferenciaLigar = undefined
        this.scope.erroLigar = undefined
        this.scope.motivoLigar = ''
        this.scope.simulacao = undefined
        this.scope.sugestoesLigar = []
        this.scope.ligarAberto = true
    }

    async #handleFecharLigar() {
        this.scope.ligarAberto = false
    }

    async #handleCnpjLigarChange(texto: string) {
        this.scope.cnpjLigar = texto
        this.scope.conferenciaLigar = undefined
        this.scope.simulacao = undefined
        this.scope.erroLigar = undefined

        if (temLetras(texto)) {
            this.#agendarSugestoes(texto, (sugestoes) => (this.scope.sugestoesLigar = sugestoes))
            return
        }
        this.scope.sugestoesLigar = []

        const cnpj = texto.replace(/\D/g, '')
        if (cnpj.length < 14) return
        if (!isValidCNPJ(cnpj)) {
            this.scope.erroLigar = 'CNPJ inválido.'
            return
        }
        // Confere sozinho quando o CNPJ fica completo.
        await this.#conferirOutra(cnpj, texto)
    }

    async #handleEscolherOutra(cnpj: string) {
        this.scope.sugestoesLigar = []
        this.scope.cnpjLigar = cnpj
        await this.#conferirOutra(cnpj, cnpj)
    }

    /** Confere a outra empresa e simula a ligação no servidor, para o diálogo dizer o que muda. */
    async #conferirOutra(cnpj: string, texto: string) {
        const situacao = this.scope.situacao
        this.scope.conferindo = true
        try {
            const outra = await service.conferirEmpresa(cnpj)
            if (texto !== this.scope.cnpjLigar) return // o operador já digitou outra coisa
            this.scope.conferenciaLigar = outra
            if (!situacao) return

            const { impedimento } = oQueMuda(situacao, outra)
            this.scope.erroLigar = impedimento
            if (impedimento) return

            this.scope.simulando = true
            const simulacao = await service.simular(situacao.empresa.cnpjMatriz, outra.cnpjMatriz)
            if (texto === this.scope.cnpjLigar) this.scope.simulacao = simulacao
        } catch (error) {
            const mensagem = mensagemDeNegocio(error)
            if (mensagem) {
                this.scope.erroLigar = mensagem
            } else {
                this.unexpected('Conferindo o CNPJ', error)
            }
        } finally {
            this.scope.conferindo = false
            this.scope.simulando = false
        }
    }

    async #handleConfirmarLigar() {
        const situacao = this.scope.situacao
        const outra = this.scope.conferenciaLigar
        if (!situacao || !outra) return
        const { modo, impedimento } = oQueMuda(situacao, outra, this.scope.simulacao)
        if (impedimento || !motivoSuficiente(this.scope.motivoLigar)) return
        const motivo = this.scope.motivoLigar.trim()

        this.scope.salvando = true
        try {
            let mensagem: string
            if (modo === 'unir') {
                const { grupoId, grupoAbsorvido } = await service.unir(
                    situacao.empresa.cnpjMatriz,
                    outra.cnpjMatriz,
                    motivo
                )
                mensagem = `Grupo ${grupoAbsorvido} unido ao grupo ${grupoId}`
            } else {
                const { resultado, grupoId } = await service.ligar(
                    situacao.empresa.cnpjMatriz,
                    outra.cnpjMatriz,
                    motivo
                )
                mensagem =
                    resultado === 'criado' ? `Grupo manual ${grupoId} criado` : `Empresas ligadas no grupo ${grupoId}`
            }
            this.scope.ligarAberto = false
            await this.#carregarSituacao(situacao.empresa.cnpjInformado)
            this.app.notify('success', mensagem)
        } catch (error) {
            const mensagem = mensagemDeNegocio(error)
            if (mensagem) {
                this.scope.erroLigar = mensagem
            } else {
                this.unexpected(modo === 'unir' ? 'Unindo os grupos' : 'Ligando as empresas', error)
            }
        } finally {
            this.scope.salvando = false
        }
    }

    // :: Tirar do grupo manual

    async #handleMotivoLigarChange(texto: string) {
        this.scope.motivoLigar = texto
    }

    async #handleAbrirRetirar() {
        this.scope.motivoRetirar = ''
        this.scope.retirarAberto = true
    }

    async #handleMotivoRetirarChange(texto: string) {
        this.scope.motivoRetirar = texto
    }

    async #handleFecharRetirar() {
        this.scope.retirarAberto = false
    }

    async #handleConfirmarRetirar() {
        const empresa = this.scope.situacao?.empresa
        if (!empresa || empresa.grupoAtual == null) return
        if (!motivoSuficiente(this.scope.motivoRetirar)) return

        this.scope.salvando = true
        try {
            await service.retirar(empresa.grupoAtual, empresa.cnpjMatriz, this.scope.motivoRetirar.trim())
            this.scope.retirarAberto = false
            await this.#carregarSituacao(empresa.cnpjInformado)
            this.app.notify('success', `Empresa retirada do grupo ${empresa.grupoAtual}`)
        } catch (error) {
            this.unexpected('Retirando a empresa do grupo', error)
        } finally {
            this.scope.salvando = false
        }
    }
}

export default GrupoManualPresenter
